"use client";

import { useLinkStatus } from "next/link";
import { ArrowRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Trailing icon for navigation CTAs, rendered inside a next/link. Shows the
 * usual arrow when idle and swaps to a spinner while the clicked navigation
 * is pending, so a click on a page that is not prefetched yet (slow
 * connection) immediately signals that something is happening.
 *
 * The two icons are stacked and hand over on the same delay, so the arrow
 * stays put until the spinner is actually visible. Unmounting it up front
 * instead would blank the icon out for every navigation that resolves inside
 * the delay window, which is all of the prefetched ones.
 *
 * With reduced motion neither animation runs, so the arrow simply stays: a
 * spinner that cannot spin is no signal, and the site disables animations
 * wholesale for that preference anyway.
 */
export function LinkArrowIndicator({ className }: { className?: string }) {
  const { pending } = useLinkStatus();

  return (
    <span
      data-testid="link-arrow-indicator"
      aria-hidden="true"
      className={cn("relative inline-flex size-4", className)}
    >
      <ArrowRight
        className={cn(
          "size-4",
          pending && "motion-safe:animate-delayed-fade-out",
        )}
      />
      {pending && (
        <span
          data-testid="link-pending-indicator"
          className="motion-safe:animate-delayed-fade-in absolute inset-0 flex opacity-0"
        >
          <Loader2 className="size-4 motion-safe:animate-spin" />
        </span>
      )}
    </span>
  );
}
